import { AlertCircle, CheckCircle2, MessageSquare, MoreHorizontal, QrCode, Settings } from 'lucide-react'
import type { DashboardStats } from '@/services/dashboardApi'
import { cn } from '@/lib/utils'
import { getChannelInfo } from './channelInfo'

export interface ChannelRowProps {
  instance: DashboardStats['instances'][number]
  onOpen: (instanceId: string) => void
  onConversations: (instanceId: string) => void
  className?: string
}

/** Fila compacta: identidad, estado con icono + texto, acción de reconexión y menú secundario. */
export function ChannelRow({ instance, onOpen, onConversations, className }: ChannelRowProps) {
  const channel = getChannelInfo(instance)
  const Icon = channel.icon
  const isOnline = instance.status === 'WORKING' || instance.status === 'CONNECTED'
  const needsReconnect = instance.status === 'DISCONNECTED' || instance.status === 'QRCODE'
  const statusLabel = isOnline
    ? 'Conectado'
    : instance.status === 'QRCODE'
      ? 'Esperando QR'
      : instance.status === 'DISCONNECTED'
        ? 'Desconectado'
        : 'Sin conectar'

  const channelType = (instance.channelType || '').toUpperCase()
  const provider = (instance.provider || (instance.metadata as Record<string, any>)?.provider || '').toLowerCase()
  const isBaileys =
    channelType === 'WHATSAPP_BAILEYS' ||
    channelType === 'WHATSAPP_WEB' ||
    channelType === 'WHATSAPP' ||
    provider === 'baileys' ||
    provider === 'evolution'

  const isCloudApi =
    !isBaileys &&
    (channelType === 'WHATSAPP_CLOUD' ||
      provider === 'meta_direct' ||
      instance.name.toLowerCase().includes('cloud'))

  const hasMissingPaymentMethod =
    isCloudApi && (instance.metadata as Record<string, any>)?.hasPaymentMethod === false

  return (
    <div className={cn('flex w-full items-center justify-between gap-3 p-4 transition-colors hover:bg-slate-50', className)}>
      <div className="flex min-w-0 items-center gap-3">
        <div className={cn('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl', channel.iconClassName)}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-bold leading-tight text-slate-800">{instance.name}</p>
          <p className="truncate text-xs font-medium text-slate-500">
            {channel.label} · {instance.profileName || 'Sin perfil'}
          </p>
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        {hasMissingPaymentMethod && (
          <span
            className="flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700"
            title="Falta método de pago en Meta Business Suite"
          >
            <AlertCircle className="h-3.5 w-3.5 text-amber-600" aria-hidden="true" />
            Falta método de pago en Meta
          </span>
        )}
        <span
          className={cn(
            'flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold',
            isOnline ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
          )}
        >
          {isOnline ? (
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {statusLabel}
        </span>

        {needsReconnect && (
          <button
            type="button"
            onClick={() => onOpen(instance.id)}
            className="flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          >
            <QrCode className="h-3.5 w-3.5" aria-hidden="true" />
            {instance.status === 'QRCODE' ? 'Ver QR' : 'Reconectar'}
          </button>
        )}

        <details className="group relative">
          <summary
            aria-label={`Más acciones de ${instance.name}`}
            className="flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand [&::-webkit-details-marker]:hidden"
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          </summary>
          <div className="absolute right-0 z-20 mt-1 w-44 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
            <button
              type="button"
              onClick={() => onOpen(instance.id)}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <Settings className="h-3.5 w-3.5" aria-hidden="true" />
              Configurar
            </button>
            <button
              type="button"
              onClick={() => onConversations(instance.id)}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
              Conversaciones
            </button>
          </div>
        </details>
      </div>
    </div>
  )
}
